import { useContext, useRef, useMemo } from 'react'
import WrappedStoryStateContext from './wrappedStoryStateContext'
import { WrappedStoryState } from './wrappedStoryStateReducer'

/**
 * A selector hook for granular access to wrapped story state.
 * Uses memoization to prevent unnecessary re-renders when the selected value
 * hasn't changed (shallow equality).
 *
 * Note: This hook will still be called on every context update, but will return
 * the cached value if the selected value hasn't changed, allowing React.memo
 * wrapped children to skip re-renders.
 *
 * @example
 * // Only causes child re-renders when data changes
 * const data = useWrappedStorySelector(state => state.data)
 *
 * // Only causes child re-renders when currentIndex changes
 * const currentIndex = useWrappedStorySelector(state => state.currentIndex)
 *
 * // For computed values, memoize the selector itself
 * const isCurrentSlide = useWrappedStorySelector(
 *   useCallback(state => state.currentSlide?.id === 'intro', [])
 * )
 */
function useWrappedStorySelector<T>(selector: (state: WrappedStoryState) => T): T {
  const state = useContext(WrappedStoryStateContext)
  const selectedValue = selector(state)

  // Track previous value to enable stable references for primitive values
  const prevValueRef = useRef<T>(selectedValue)
  const prevSelectorRef = useRef(selector)

  // Return cached value if selector and result are equivalent
  // This helps React.memo children avoid re-renders
  const cachedValue = useMemo(() => {
    const prevValue = prevValueRef.current
    const prevSelector = prevSelectorRef.current

    // If selector changed, always use new value
    if (prevSelector !== selector) {
      prevSelectorRef.current = selector
      prevValueRef.current = selectedValue
      return selectedValue
    }

    // For primitive values, use strict equality
    if (Object.is(prevValue, selectedValue)) {
      return prevValue
    }

    // For objects/arrays, shallow compare (reference check)
    // If reference changed, update cache
    prevValueRef.current = selectedValue
    return selectedValue
  }, [selector, selectedValue])

  return cachedValue
}

export default useWrappedStorySelector
